/**
 * The files a passing server build checked that this tab does not hold.
 *
 * Chef runs the writes and the build, and streams each write here as a `serverWrite` annotation.
 * A write that never arrives left the tab without a file the build had passed with: Ledgerly v3
 * (1 Oct 2026) saved a version whose page imported a `ProductPreview.tsx` the tab never received,
 * and crashed on load. Every passing build now lists its source files; any listed here as missing
 * is reported as drift, so the turn's files are fetched from the server before the snapshot.
 */
export function missingServerBuiltFiles(
  paths: unknown,
  held: Readonly<Record<string, unknown>>,
): string[] {
  if (!Array.isArray(paths)) return [];
  return paths.filter(
    (path): path is string =>
      typeof path === 'string' && path.startsWith('src/') && held[path] === undefined,
  );
}

/**
 * Whether a server-written turn's files can be trusted without reading them back from the
 * builder.
 *
 * Only while the turn is still running. Once its build has passed (`verified`), the snapshot
 * taken next records the tab's files as that version, so they are always compared with the
 * builder's: a file can arrive with content the build did not pass, which no missing-file check
 * sees. On production (24 Oct 2026) a scope's tab held `mockData.ts` with a block written twice
 * while the builder's copy had it once (see `chefWriteHandling`), and saved that copy as the
 * verified version. The light preview could not compile it, and every reload showed "No saved
 * version".
 */
export function canSkipServerFileSync(state: {
  verified: boolean;
  delivery: 'annotation' | 'client';
  driftCount: number;
  heldFileCount: number;
}): boolean {
  if (state.verified) return false;
  return state.delivery === 'annotation' && state.driftCount === 0 && state.heldFileCount > 0;
}

const CHEF_WRITE_TOOLS: ReadonlySet<string> = new Set(['writeFile', 'searchReplace', 'deleteFile']);

/**
 * What the browser does with a write tool call when Chef ran the writes this turn (P6.4d-3).
 *
 * - `client`: not a Chef write; the browser's own tool handler runs it.
 * - `apply`: Chef ran it and streams no files (`writeDelivery: client`), so the browser repeats
 *   the call to mirror it.
 * - `record`: Chef ran it and streams the resulting file as a `serverWrite` annotation. Only the
 *   timeline step is added. Running the call here as well applied an edit twice whenever its
 *   annotation landed first: an insert (`newString` containing `oldString`) went in again on top
 *   of Chef's result. The AI SDK calls `onToolCall` for these, since Chef's tools are not
 *   provider-executed. That kept a block twice in a tab's `mockData.ts` on production
 *   (24 Oct 2026, Bug 893).
 */
export function chefWriteHandling(call: {
  toolName: string;
  serverWrites: boolean;
  delivery: 'annotation' | 'client';
}): 'client' | 'apply' | 'record' {
  if (!call.serverWrites || !CHEF_WRITE_TOOLS.has(call.toolName)) return 'client';
  return call.delivery === 'annotation' ? 'record' : 'apply';
}
